// FillBlankQuestion.tsx - Premium Fill-in-the-Blank Question Component
// Features text inputs seamlessly integrated within the question text
/* eslint-disable @typescript-eslint/no-explicit-any */


import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Animated,
  Keyboard
} from 'react-native';
import ReAnimated from 'react-native-reanimated';

import { useFadeIn, useCorrectPulse, useWrongShake } from '../../../design-system/animations';
import { DSText } from '../../../design-system/components/DSText';
import { useAppTheme } from '../../../design-system/ThemeProvider';
import { spacing, radius } from '../../../design-system/tokens';
import { useLanguageStore } from '../../../features/quiz-core/languageStore';
import { t } from '../../../i18n/translations';
import { PartialCreditService } from '../../../shared/services/partialCreditService';
import { IQESParchmentCard, IQESSceneBackdrop } from '../../../shared/ui/IQESComponents';
import { iqesColors, iqesTypography } from '../../../shared/ui/iqesDesignSystem';
import { inferIQESSceneKey } from '../../../shared/ui/iqesSceneSystem';
import { playIQESAudioCue, playIQESHaptic } from '../../../shared/ui/iqesSensoryFeedback';
import useReducedMotion from '../../../shared/ui/useReducedMotion';
import { FillBlankFormatData } from '../../../types/questionFormats';
import DevotionalExplanation from '../DevotionalExplanation';

import { FillBlankQuestionProps, FillBlankAnswer } from './types/QuestionFormatProps';

const FillBlankQuestion: React.FC<FillBlankQuestionProps> = ({
  question,
  currentIndex,
  totalQuestions,
  onAnswer,
  answers = {},
  disabled = false,
  showCorrectAnswer = false,
  highlightCorrect: _highlightCorrect = false,
  showExplanation: _showExplanation = false,
  timeSpent = 0,
  isAnswered = false,
  inputStyle = 'underline',
  showCharacterCount = false,
  autoCapitalize = true,
  spellCheck = true,
  maxLength = 50,
  placeholder = '',
  animationDuration = 300,
  hapticFeedback = true,
  theme = 'premium',
  showProgressIndicator: _showProgressIndicator = true,
  accessibilityLabel,
  accessibilityHint,
  explanation
}) => {
  const { colors, isDark } = useAppTheme();
  const { language } = useLanguageStore();
  const reduceMotion = useReducedMotion();
  const styles = createStyles(colors, isDark);
  const [localAnswers, setLocalAnswers] = useState<Record<string, string>>(answers);
  const [focusedBlankId, setFocusedBlankId] = useState<string | null>(null);
  const [animatedValues, setAnimatedValues] = useState(() =>
    new Map(question.format_data.blanks.map(blank => [
      blank.id,
      {
        borderColor: new Animated.Value(0),
        scale: new Animated.Value(1),
        backgroundColor: new Animated.Value(0)
      }
    ]))
  );

  const inputRefs = useRef<Map<string, TextInput>>(new Map());
  const scrollViewRef = useRef<ScrollView>(null);

  const formatData = question.format_data as FillBlankFormatData;
  const isScreenshotSmokeQuestion = Number(question.id) >= 9100 && Number(question.id) < 9200;
  const screenshotPhase = (question.format_data as FillBlankFormatData & { screenshot_phase?: string }).screenshot_phase;
  const isScreenshotTypingPhase = isScreenshotSmokeQuestion && screenshotPhase === 'typing';
  const _progress = totalQuestions > 0 ? (currentIndex + 1) / totalQuestions : 0;
  const totalBlanks = formatData.blanks.length;
  const filledBlanks = Object.keys(localAnswers).filter(id => localAnswers[id]?.trim()).length;
  const referenceText = String(formatData.reference || formatData.verse_reference || question.verse_reference || '');
  const sceneKey = useMemo(
    () => inferIQESSceneKey({
      question: question.question,
      type: 'fill_blank',
      category: question.category,
      verseReference: question.verse_reference || formatData.verse_reference || formatData.reference,
    }),
    [formatData.reference, formatData.verse_reference, question.category, question.question]
  );

  // DS entrance animation for the question card
  const fadeInStyle = useFadeIn();

  // Compute correctness flags for result feedback animations
  const allCorrect = useMemo(() => {
    if (!showCorrectAnswer) return false;
    return formatData.blanks.every((blank) => {
      const value = (localAnswers[blank.id] || '').toLowerCase().trim();
      return value === blank.answer.toLowerCase().trim() ||
        (blank.alternates && blank.alternates.some((alt: string) =>
          value === alt.toLowerCase().trim()));
    });
  }, [showCorrectAnswer, localAnswers, formatData.blanks]);

  const hasWrong = useMemo(() => {
    if (!showCorrectAnswer) return false;
    return formatData.blanks.some((blank) => {
      const value = (localAnswers[blank.id] || '').trim();
      if (!value) return true; // empty is wrong
      const lower = value.toLowerCase();
      const isCorrect = lower === blank.answer.toLowerCase().trim() ||
        (blank.alternates && blank.alternates.some((alt: string) =>
          lower === alt.toLowerCase().trim()));
      return !isCorrect;
    });
  }, [showCorrectAnswer, localAnswers, formatData.blanks]);

  const replaceTemplate = useCallback((template: string, values: Record<string, string | number>) => {
    return Object.entries(values).reduce(
      (output, [key, value]) => output.replace(new RegExp(`{{${key}}}`, 'g'), String(value)),
      template
    );
  }, []);

  const isBlankCorrect = useCallback((blank: FillBlankFormatData['blanks'][number], value: string) => {
    const normalizedValue = value.toLowerCase().trim();
    return normalizedValue === blank.answer.toLowerCase().trim() ||
      Boolean(blank.alternates?.some((alt: string) => normalizedValue === alt.toLowerCase().trim()));
  }, []);

  const { animatedStyle: correctPulseStyle } = useCorrectPulse(allCorrect);
  const wrongShakeStyle = useWrongShake(hasWrong);

  // Track the current question to detect actual question changes
  const currentQuestionRef = useRef<string>('');

  // Reset state only when question actually changes - prevents clearing while typing
  useEffect(() => {
    const questionKey = `${question.id || currentIndex}-${question.question || ''}`;

    // Only reset if this is actually a new question
    if (currentQuestionRef.current !== questionKey) {
      currentQuestionRef.current = questionKey;

      // Reset to the provided answers (empty object for new questions)
      setLocalAnswers(answers || {});

      // Reset focused state
      setFocusedBlankId(null);

      // Reset animated values for new question blanks
      const newAnimatedValues = new Map(question.format_data.blanks.map(blank => [
        blank.id,
        {
          borderColor: new Animated.Value(0),
          scale: new Animated.Value(1),
          backgroundColor: new Animated.Value(0)
        }
      ]));
      setAnimatedValues(newAnimatedValues);

      // Clear input refs for new question
      inputRefs.current.clear();
    }
  }, [question.id, currentIndex, question.question]); // Removed 'answers' from dependencies to prevent clearing while typing

  // Separate effect to handle answers prop changes (without full reset)
  useEffect(() => {
    if (answers && Object.keys(answers).length > 0) {
      setLocalAnswers(prevAnswers => {
        // Only update if we don't already have answers (initial load scenario)
        const hasExistingAnswers = Object.keys(prevAnswers).some(key => prevAnswers[key]?.trim());
        if (!hasExistingAnswers) {
          return { ...prevAnswers, ...answers };
        }
        return prevAnswers;
      });
    }
  }, [answers]);

  // Animation helpers
  const animateBlank = useCallback((blankId: string, focused: boolean, correct?: boolean, incorrect?: boolean) => {
    const values = animatedValues.get(blankId);
    if (!values) return;

    let borderColorValue = 0; // Default gray
    let backgroundValue = 0;   // Default transparent

    if (incorrect) {
      borderColorValue = 3; // Red
      backgroundValue = 3;  // Light red
    } else if (correct) {
      borderColorValue = 2; // Green
      backgroundValue = 2;  // Light green
    } else if (focused) {
      borderColorValue = 1; // Blue
      backgroundValue = 1;  // Light blue
    }

    Animated.parallel([
      Animated.timing(values.borderColor, {
        toValue: borderColorValue,
        duration: animationDuration / 2,
        useNativeDriver: false
      }),
      Animated.timing(values.backgroundColor, {
        toValue: backgroundValue,
        duration: animationDuration / 2,
        useNativeDriver: false
      }),
      Animated.spring(values.scale, {
        toValue: focused ? 1.02 : 1,
        useNativeDriver: true,
        speed: 20,
        bounciness: 4
      })
    ]).start();
  }, [animatedValues, animationDuration]);

  // Animate blanks when showing correct answers
  useEffect(() => {
    if (showCorrectAnswer) {
      formatData.blanks.forEach((blank) => {
        const value = localAnswers[blank.id] || '';
        const isCorrect = value.toLowerCase().trim() === blank.answer.toLowerCase().trim() ||
          (blank.alternates && blank.alternates.some((alt: string) =>
            value.toLowerCase().trim() === alt.toLowerCase().trim()
          ));
        const isIncorrect = Boolean(value.trim() && !isCorrect);

        animateBlank(blank.id, false, isCorrect, isIncorrect);
      });
    }
  }, [showCorrectAnswer, localAnswers, formatData.blanks, animateBlank]);

  // Handle text input change
  const handleTextChange = useCallback((blankId: string, text: string) => {
    if (disabled || isAnswered) return;

    const newAnswers = { ...localAnswers, [blankId]: text };
    setLocalAnswers(newAnswers);

    // Auto-submit is disabled - user must click submit button for better UX
    // const newFilledCount = Object.keys(newAnswers).filter(id => newAnswers[id]?.trim()).length;
    // if (newFilledCount === totalBlanks) {
    //   const fillBlankAnswer: FillBlankAnswer = {
    //     format: 'fill_blank',
    //     answers: newAnswers,
    //     confidence_level: 3,
    //     time_spent_seconds: timeSpent
    //   };
    //
    //   // Small delay to allow user to see their input
    //   setTimeout(() => onAnswer(fillBlankAnswer), 500);
    // }

    const animatedValue = animatedValues.get(blankId);
    if (!reduceMotion && animatedValue && text.length > (localAnswers[blankId] || '').length) {
      Animated.sequence([
        Animated.spring(animatedValue.scale, {
          toValue: 1.045,
          useNativeDriver: true,
          speed: 24,
          bounciness: 5,
        }),
        Animated.spring(animatedValue.scale, {
          toValue: focusedBlankId === blankId ? 1.02 : 1,
          useNativeDriver: true,
          speed: 24,
          bounciness: 4,
        }),
      ]).start();
    }

    if (text.length > 0 && localAnswers[blankId] === '' && hapticFeedback) {
      void playIQESHaptic('type', { enabled: !reduceMotion });
      void playIQESAudioCue('select', { enabled: !reduceMotion });
    }
  }, [disabled, isAnswered, localAnswers, animatedValues, reduceMotion, focusedBlankId, hapticFeedback]);

  // Handle focus change
  const handleFocusChange = useCallback((blankId: string, focused: boolean) => {
    setFocusedBlankId(focused ? blankId : null);
    animateBlank(blankId, focused);

    if (focused) {
      // Scroll to focused input if needed
      setTimeout(() => {
        const inputRef = inputRefs.current.get(blankId);
        if (inputRef && scrollViewRef.current) {
          inputRef.measureLayout(
            scrollViewRef.current as any,
            (x, y) => {
              scrollViewRef.current?.scrollTo({ y: y - 100, animated: true });
            },
            () => {} // On fail
          );
        }
      }, 100);
    }
  }, [animateBlank]);

  // Move to next input
  const moveToNextInput = useCallback((currentBlankId: string) => {
    const blankIds = formatData.blanks.map(blank => blank.id);
    const currentIndex = blankIds.indexOf(currentBlankId);
    const nextIndex = currentIndex + 1;

    if (nextIndex < blankIds.length) {
      const nextBlankId = blankIds[nextIndex];
      const nextInputRef = inputRefs.current.get(nextBlankId);
      nextInputRef?.focus();
    } else {
      // Last input, dismiss keyboard
      Keyboard.dismiss();
    }
  }, [formatData.blanks]);

  // Get input style based on state
  const getInputStyle = (blank: any) => {
    const value = localAnswers[blank.id] || '';
    const isFocused = focusedBlankId === blank.id;
    const isCorrect = showCorrectAnswer && (
      value.toLowerCase().trim() === blank.answer.toLowerCase().trim() ||
      (blank.alternates && blank.alternates.some((alt: string) =>
        value.toLowerCase().trim() === alt.toLowerCase().trim()
      ))
    );
    const isIncorrect = showCorrectAnswer && value.trim() && !isCorrect;
    const isEmpty = !value.trim();

    // Animation is now handled by useEffect to avoid setState during render

    const animatedValue = animatedValues.get(blank.id);

    return [
      styles.input,
      inputStyle === 'box' ? styles.inputBox :
      inputStyle === 'rounded' ? styles.inputRounded : styles.inputUnderline,
      theme === 'premium' && styles.premiumInput,
      isEmpty && styles.inputEmpty,
      isFocused && styles.inputFocused,
      isCorrect && styles.inputCorrect,
      isIncorrect && styles.inputIncorrect,
      disabled && styles.inputDisabled,
      {
        borderColor: animatedValue?.borderColor.interpolate({
          inputRange: [0, 1, 2, 3],
          outputRange: [colors.outlineVariant, colors.primary, colors.correct, colors.incorrect]
        }),
        backgroundColor: animatedValue?.backgroundColor.interpolate({
          inputRange: [0, 1, 2, 3],
          outputRange: [colors.transparent, 'rgba(79, 70, 229, 0.05)', 'rgba(16, 185, 129, 0.05)', 'rgba(239, 68, 68, 0.05)']
        })
      }
    ];
  };

  // Parse template to create components with inputs
  const renderBlankField = (blank: FillBlankFormatData['blanks'][number], index: number) => {
    const animatedValue = animatedValues.get(blank.id);
    const value = localAnswers[blank.id] || '';
    const hasValue = Boolean(value.trim());
    const isCorrect = showCorrectAnswer && isBlankCorrect(blank, value);
    const isIncorrect = showCorrectAnswer && hasValue && !isCorrect;
    const isMissed = showCorrectAnswer && !hasValue;
    const resolvedAnswer = blank.answer;
    const reviewLabel = isCorrect
      ? t('quizFlow.correct', language) || 'Correct!'
      : isIncorrect
        ? t('quizFlow.correctAnswer', language) || 'Correct answer'
        : t('quizFlow.incorrect', language) || 'Incorrect';

    if (showCorrectAnswer) {
      return (
        <Animated.View
          key={`review-${blank.id}`}
          style={[
            styles.inputWrapper,
            animatedValue && {
              transform: [{ scale: animatedValue.scale }]
            }
          ]}
        >
          <View style={[
            styles.reviewBlankChip,
            isCorrect && styles.reviewBlankChipCorrect,
            (isIncorrect || isMissed) && styles.reviewBlankChipIncorrect,
          ]}>
            <DSText
              variant="caption"
              color={isCorrect ? colors.correctDark : colors.incorrectDark}
              style={styles.reviewBlankLabel}
            >
              {reviewLabel}
            </DSText>
            <DSText
              variant="body"
              color={isCorrect ? colors.correctDark : colors.onSurface}
              style={styles.reviewBlankAnswer}
            >
              {isCorrect ? value.trim() : resolvedAnswer}
            </DSText>
            {(isIncorrect || isMissed) && hasValue && (
              <DSText variant="caption" color={colors.onSurfaceVariant} style={styles.reviewBlankPrevious}>
                {`${t('quizFlow.yourAnswer', language) || 'Your answer'}: ${value.trim()}`}
              </DSText>
            )}
          </View>
        </Animated.View>
      );
    }

    return (
      <Animated.View
        key={`input-${blank.id}`}
        style={[
          styles.inputWrapper,
          animatedValue && {
            transform: [{ scale: animatedValue.scale }]
          }
        ]}
      >
        {focusedBlankId === blank.id && <View style={styles.inputFocusAura} pointerEvents="none" />}
        <TextInput
          ref={(ref) => {
            if (ref) {
              inputRefs.current.set(blank.id, ref);
            } else {
              inputRefs.current.delete(blank.id);
            }
          }}
          style={getInputStyle(blank)}
          value={value}
          onChangeText={(text) => handleTextChange(blank.id, text)}
          onFocus={() => handleFocusChange(blank.id, true)}
          onBlur={() => handleFocusChange(blank.id, false)}
          onSubmitEditing={() => moveToNextInput(blank.id)}
          placeholder={placeholder || '____'}
          placeholderTextColor="#5B5367"
          editable={!disabled && !isAnswered}
          autoCapitalize={autoCapitalize ? 'sentences' : 'none'}
          spellCheck={spellCheck}
          maxLength={maxLength}
          returnKeyType={index < formatData.blanks.length - 1 ? 'next' : 'done'}
          accessibilityLabel={`Blank ${index + 1}: type your answer`}
          accessibilityHint="Fill in the missing word"
          accessibilityRole="search"
        />

        {showCharacterCount && focusedBlankId === blank.id && (
          <DSText variant="micro" color={colors.outline} style={styles.characterCount}>
            {(localAnswers[blank.id] || '').length}/{maxLength}
          </DSText>
        )}
      </Animated.View>
    );
  };

  const renderQuestionWithBlanks = () => {
    // Use question.question as the template if formatData.template is not available
    const template = formatData.template || question.question || '';

    if (isScreenshotSmokeQuestion && !showCorrectAnswer) {
      const displayText = template
        .replace(/\{blank\d+\}/g, '______')
        .replace(/\{[^}]+\}/g, '______')
        .replace(/\s+that he gave/i, '\nthat he gave')
        .replace(/\s+Son\./i, '\nSon.');

      return (
        <View style={styles.questionWithBlanks}>
          <DSText key="screenshot-question-text" variant="body" style={styles.screenshotQuestionText}>
            {displayText}
          </DSText>
        </View>
      );
    }

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    // Check for both formats: {blank.id} and {blank1}, {blank2}, etc.
    const hasIdPlaceholders = formatData.blanks.some(blank => template.includes(`{${blank.id}}`));
    const hasNumberedPlaceholders = /\{blank\d+\}/.test(template);

    if (!template || (!hasIdPlaceholders && !hasNumberedPlaceholders)) {
      // Fallback: Show question text and then input fields
      if (question.question) {
        parts.push(
          <DSText key="question-text" variant="body" style={styles.questionTextInline}>
            {question.question}
          </DSText>
        );
      }

      // Add all blanks as separate inputs
      formatData.blanks.forEach((blank, index) => {
        parts.push(renderBlankField(blank, index));
      });

      return (
        <View style={styles.questionWithBlanks}>
          {parts.filter(part => part !== null && part !== undefined)}
        </View>
      );
    }

    // Handle numbered placeholders {blank1}, {blank2}, etc.
    if (hasNumberedPlaceholders) {
      // Create a working copy of the template
      let workingTemplate = template;
      let blankCounter = 1;

      // Process each numbered placeholder in order
      formatData.blanks.forEach((blank, index) => {
        const numberedPlaceholder = `{blank${blankCounter}}`;
        const placeholderIndex = workingTemplate.indexOf(numberedPlaceholder);

        if (placeholderIndex !== -1) {
          // Add text before the blank
          if (placeholderIndex > 0) {
            const textContent = workingTemplate.substring(0, placeholderIndex);
            if (textContent && textContent.trim()) {
              parts.push(
                <DSText key={`text-${index}-before`} variant="body" style={styles.questionTextInline}>
                  {textContent}
                </DSText>
              );
            }
          }

          // Add the input
          parts.push(renderBlankField(blank, index));

          // Update working template for next iteration
          workingTemplate = workingTemplate.substring(placeholderIndex + numberedPlaceholder.length);
          blankCounter++;
        }
      });

      // Add any remaining text
      if (workingTemplate && workingTemplate.trim()) {
        parts.push(
          <DSText key="text-final" variant="body" style={styles.questionTextInline}>
            {workingTemplate}
          </DSText>
        );
      }
    } else {
      // Original logic for ID-based placeholders
      formatData.blanks.forEach((blank, index) => {
        const blankPlaceholder = `{${blank.id}}`;
        const blankIndex = template.indexOf(blankPlaceholder, lastIndex);

        if (blankIndex !== -1) {
          // Add text before the blank
          if (blankIndex > lastIndex) {
            const textContent = template.substring(lastIndex, blankIndex);
            if (textContent && textContent.trim()) {
              parts.push(
                <DSText key={`text-${index}-before`} variant="body" style={styles.questionTextInline}>
                  {textContent}
                </DSText>
              );
            }
          }

        // Add the input
        parts.push(renderBlankField(blank, index));

          lastIndex = blankIndex + blankPlaceholder.length;
        }
      });

      // Add remaining text after last blank
      if (lastIndex < template.length) {
        const remainingText = template.substring(lastIndex);
        // Only add if there's actual content, not just whitespace or a single period
        if (remainingText && remainingText.trim() && remainingText.trim() !== '.') {
          parts.push(
            <DSText key="text-final" variant="body" style={styles.questionTextInline}>
              {remainingText}
            </DSText>
          );
        } else if (remainingText.trim() === '.') {
          // If it's just a period, include it in the last text element
          if (parts.length > 0 && React.isValidElement(parts[parts.length - 1])) {
            const lastElement = parts[parts.length - 1] as React.ReactElement<{ children?: React.ReactNode }>;
            if (lastElement.type === Text || lastElement.type === DSText) {
              const existingChildren = typeof lastElement.props.children === 'string'
                ? lastElement.props.children
                : '';
              // Append the period to the previous text element
              parts[parts.length - 1] = React.cloneElement(lastElement, {
                children: `${existingChildren}.`
              });
            }
          }
        }
      }
    }

    return (
      <View style={styles.questionWithBlanks}>
        {parts.filter(part => part !== null && part !== undefined)}
      </View>
    );
  };

  // Manual submit button with partial credit calculation
  const handleManualSubmit = useCallback(() => {
    if (disabled || isAnswered || filledBlanks === 0) return;

    // Calculate partial credit if enabled
    let partialScores: Record<string, number> | undefined;

    if (formatData.partial_credit) {
      const correctAnswersMap = formatData.blanks.reduce((acc, blank) => {
        acc[blank.id] = {
          answer: blank.answer,
          alternates: blank.alternates,
          points: blank.points || 1
        };
        return acc;
      }, {} as Record<string, { answer: string; alternates?: string[]; points?: number }>);

      const partialResult = PartialCreditService.calculateFillBlankScore(
        localAnswers,
        correctAnswersMap,
        formatData.case_sensitive,
        formatData.partial_credit_threshold || 70
      );

      partialScores = partialResult.breakdown;
    }

    const fillBlankAnswer: FillBlankAnswer = {
      format: 'fill_blank',
      answers: localAnswers,
      confidence_level: filledBlanks === totalBlanks ? 4 : 2,
      time_spent_seconds: timeSpent,
      partial_scores: partialScores
    };

    if (hapticFeedback) {
      void playIQESHaptic('submit', { enabled: !reduceMotion });
      void playIQESAudioCue('submit', { enabled: !reduceMotion });
    }

    onAnswer(fillBlankAnswer);
  }, [disabled, isAnswered, filledBlanks, totalBlanks, localAnswers, timeSpent, onAnswer, formatData, hapticFeedback, reduceMotion]);

  return (
    <View
      testID="fill-blank-screen"
      style={styles.container}
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
    >
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        contentInsetAdjustmentBehavior="automatic"
      >
        {/* Question with integrated blanks */}
        <ReAnimated.View style={[
            fadeInStyle,
            allCorrect && correctPulseStyle,
            hasWrong && wrongShakeStyle,
          ]}
        >
          <IQESSceneBackdrop sceneKey={sceneKey} style={styles.sceneQuestionPanel}>
            <View style={styles.sceneScrim}>
              <View style={styles.formatBadge}>
                <DSText variant="caption" color={iqesColors.goldSoft} style={styles.formatBadgeText}>
                  {t('quizFlow.format_fill_blank', language) || 'FILL IN THE BLANK'}
                </DSText>
              </View>
              <IQESParchmentCard prompt="" reference={referenceText} style={styles.questionContainer}>
                {renderQuestionWithBlanks()}
              </IQESParchmentCard>
              <View style={styles.answerTray}>
                <DSText variant="caption" color={iqesColors.mutedWarm} align="center" style={styles.answerTrayLabel}>
                  {t('quizFlow.typeYourAnswer', language)}
                </DSText>
                <DSText variant="title" color={iqesColors.ivory} align="center" style={styles.answerTrayBlanks}>
                  {isScreenshotTypingPhase
                    ? String(localAnswers[formatData.blanks[0]?.id] || '').padEnd(5, '_').split('').join('  ')
                    : filledBlanks > 0
                    ? replaceTemplate(
                      t('quizFlow.blanksFilled', language) || '{{filled}} of {{total}} blanks filled',
                      { filled: filledBlanks, total: totalBlanks }
                    )
                    : '______'}
                </DSText>
              </View>
              {isScreenshotTypingPhase && (
                <View style={styles.screenshotKeyboard} pointerEvents="none">
                  {['Q W E R T Y U I O P', 'A S D F G H J K L', 'Z X C V B N M'].map((row) => (
                    <View key={row} style={styles.keyboardRow}>
                      {row.split(' ').map((letter) => (
                        <View key={letter} style={styles.keyboardKey}>
                          <Text style={styles.keyboardKeyText}>{letter}</Text>
                        </View>
                      ))}
                    </View>
                  ))}
                  <View style={styles.keyboardBottomRow}>
                    <View style={[styles.keyboardKey, styles.keyboardUtilityKey]}>
                      <Text style={styles.keyboardKeyText}>123</Text>
                    </View>
                    <View style={[styles.keyboardKey, styles.keyboardSpaceKey]}>
                      <Text style={styles.keyboardKeyText}>{t('quizFlow.keyboardSpace', language)}</Text>
                    </View>
                    <View style={[styles.keyboardKey, styles.keyboardUtilityKey]}>
                      <Text style={styles.keyboardKeyText}>{t('quizFlow.keyboardReturn', language)}</Text>
                    </View>
                  </View>
                </View>
              )}
            </View>
          </IQESSceneBackdrop>
        </ReAnimated.View>

        {/* Submit button - always show when not answered and has some content */}
        {!isAnswered && filledBlanks > 0 && (
          <View style={styles.submitContainer}>
            <TouchableOpacity
              style={[
                styles.submitButton,
                filledBlanks > 0 ? styles.submitButtonEnabled : styles.submitButtonDisabled
              ]}
              onPress={handleManualSubmit}
              disabled={filledBlanks === 0 || disabled}
              accessibilityLabel={replaceTemplate(t('quizFlow.submitFillBlankAnswersAccessibility', language), {
                filled: filledBlanks,
                total: totalBlanks,
              })}
              accessibilityRole="button"
              accessibilityState={{ disabled: filledBlanks === 0 || disabled }}
            >
              <DSText
                variant="body"
                color={filledBlanks > 0 ? colors.onPrimary : colors.outline}
                style={styles.submitButtonText}
              >
                {`${t('quizFlow.submit', language) || 'Submit'} (${filledBlanks}/${totalBlanks})`}
              </DSText>
            </TouchableOpacity>
          </View>
        )}

        {/* Devotional Explanation */}
        {showCorrectAnswer && explanation && (
          <DevotionalExplanation explanation={String(explanation || '')} />
        )}

      </ScrollView>
    </View>
  );
};

const createStyles = (colors: any, isDark: boolean) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.transparent,
  },
  scrollContainer: {
    flex: 1,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xs,
  },

  // Progress Section
  progressSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xl,
    paddingHorizontal: spacing.lg,
  },
  progressBadge: {
    backgroundColor: colors.primaryContainer,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.lg,
    marginRight: spacing.md,
    borderWidth: 1,
    borderColor: 'rgba(79, 70, 229, 0.3)',
  },
  progressText: {
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  progressBarContainer: {
    flex: 1,
    height: spacing.sm,
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    borderRadius: radius.sm,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(79, 70, 229, 0.2)',
  },
  progressBar: {
    height: '100%',
    backgroundColor: colors.primary,
    borderRadius: spacing.xs,
  },

  // Instructions — subtle tonal card, not gradient
  instructionContainer: {
    marginBottom: spacing.lg,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderRadius: radius.lg,
    backgroundColor: 'rgba(41, 25, 8, 0.72)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.34)',
    alignItems: 'center',
  },
  instructionText: {
    fontWeight: '600' as const,
    textAlign: 'center' as const,
    marginBottom: spacing.xs,
    letterSpacing: 0.3,
    color: iqesColors.goldSoft,
  },

  // Question Container — soft DS card
  questionContainer: {
    width: '100%',
    minHeight: 188,
    marginTop: 0,
    marginBottom: spacing.sm,
  },
  sceneQuestionPanel: {
    borderRadius: radius.xl,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.38)',
    marginBottom: spacing.md,
  },
  sceneScrim: {
    padding: spacing.sm,
    backgroundColor: 'rgba(5, 7, 18, 0.5)',
  },
  formatBadge: {
    alignSelf: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: 5,
    borderRadius: radius.full,
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.5)',
    backgroundColor: 'rgba(41, 25, 8, 0.78)',
    marginBottom: spacing.sm,
  },
  formatBadgeText: {
    fontWeight: '900',
    letterSpacing: 0.5,
  },
  answerTray: {
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.32)',
    backgroundColor: 'rgba(5, 7, 18, 0.72)',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  answerTrayLabel: {
    marginBottom: spacing.xs,
  },
  answerTrayBlanks: {
    fontWeight: '800',
    letterSpacing: 3,
  },
  screenshotKeyboard: {
    marginTop: spacing.sm,
    borderRadius: radius.lg,
    padding: spacing.xs,
    backgroundColor: 'rgba(10, 12, 20, 0.86)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
  },
  keyboardRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 4,
    marginBottom: 4,
  },
  keyboardBottomRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 5,
  },
  keyboardKey: {
    minWidth: 25,
    height: 26,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
  },
  keyboardUtilityKey: {
    minWidth: 48,
  },
  keyboardSpaceKey: {
    minWidth: 150,
  },
  keyboardKeyText: {
    color: '#FFF7ED',
    fontSize: 11,
    lineHeight: 13,
    fontWeight: '700',
  },

  // Question with blanks - improved text flow alignment
  questionWithBlanks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    justifyContent: 'center',
  },
  questionTextInline: {
    fontSize: 21,
    fontWeight: '600',
    color: iqesColors.parchmentInk,
    fontFamily: iqesTypography.displayFamily,
    lineHeight: 34,
    flexShrink: 1,
    flexGrow: 0,
    textAlign: 'center',
  },
  screenshotQuestionText: {
    maxWidth: '92%',
    fontSize: 19,
    fontWeight: '600',
    color: iqesColors.parchmentInk,
    fontFamily: iqesTypography.displayFamily,
    lineHeight: 31,
    flexShrink: 1,
    textAlign: 'center',
  },

  // Input styles - improved for inline text flow
  inputWrapper: {
    marginHorizontal: 1,
    marginVertical: 0,
    position: 'relative',
  },
  inputFocusAura: {
    position: 'absolute',
    left: -5,
    right: -5,
    top: -5,
    bottom: -5,
    borderRadius: radius.md,
    backgroundColor: 'rgba(251, 191, 36, 0.24)',
    borderWidth: 1,
    borderColor: 'rgba(253, 230, 138, 0.5)',
  },
  input: {
    minWidth: 68,
    maxWidth: 120,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    fontSize: 20,
    fontWeight: '800',
    color: iqesColors.parchmentInk,
    textAlign: 'center',
    flexShrink: 1,
    height: 34,
  },
  inputUnderline: {
    borderBottomWidth: 3,
    borderBottomColor: '#7C3AED',
    backgroundColor: colors.transparent,
  },
  inputBox: {
    borderWidth: 2,
    borderColor: colors.outlineVariant,
    borderRadius: radius.sm,
    backgroundColor: colors.surfaceContainerHighest,
  },
  inputRounded: {
    borderWidth: 2,
    borderColor: colors.outlineVariant,
    borderRadius: radius.lg,
    backgroundColor: colors.surfaceContainerHighest,
  },
  premiumInput: {
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  inputEmpty: {
    backgroundColor: isDark ? colors.surfaceContainerHighest : colors.surfaceContainerLow,
  },
  inputFocused: {
    borderColor: iqesColors.gold,
    backgroundColor: 'rgba(251, 191, 36, 0.11)',
    shadowColor: iqesColors.gold,
    shadowOpacity: 0.24,
  },
  inputCorrect: {
    borderColor: colors.correct,
    backgroundColor: 'rgba(16, 185, 129, 0.05)',
  },
  inputIncorrect: {
    borderColor: colors.incorrect,
    backgroundColor: 'rgba(239, 68, 68, 0.05)',
  },
  inputDisabled: {
    backgroundColor: colors.surfaceContainerHigh,
    borderColor: colors.outlineVariant,
    color: colors.outline,
  },

  // Character count
  characterCount: {
    position: 'absolute',
    bottom: -16,
    right: 0,
    fontWeight: '500',
  },

  // Submit button
  submitContainer: {
    marginBottom: spacing.lg,
  },
  submitButton: {
    backgroundColor: colors.primary,
    borderRadius: radius.lg,
    padding: spacing.lg,
    alignItems: 'center',
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  submitButtonEnabled: {
    backgroundColor: colors.primary,
  },
  submitButtonDisabled: {
    backgroundColor: colors.surfaceContainerHigh,
    shadowOpacity: 0,
    elevation: 0,
  },
  submitButtonText: {
    fontWeight: '700',
    letterSpacing: 0.5,
  },

  reviewBlankChip: {
    minWidth: 72,
    maxWidth: 180,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    backgroundColor: colors.surfaceContainerLow,
  },
  reviewBlankChipCorrect: {
    borderColor: colors.correct,
    backgroundColor: colors.correctContainer,
  },
  reviewBlankChipIncorrect: {
    borderColor: colors.incorrect,
    backgroundColor: colors.incorrectContainer,
  },
  reviewBlankLabel: {
    fontWeight: '700',
    marginBottom: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  reviewBlankAnswer: {
    fontWeight: '700',
    lineHeight: 20,
  },
  reviewBlankPrevious: {
    marginTop: 2,
    fontWeight: '500',
  },

  // Partial credit container
  partialCreditContainer: {
    marginBottom: spacing.md,
    padding: spacing.md,
    backgroundColor: colors.correctContainer,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: 'rgba(16, 185, 129, 0.1)',
  },
  partialCreditNote: {
    fontStyle: 'italic',
  },

  // Note container
  noteContainer: {
    padding: spacing.md,
    backgroundColor: colors.surfaceContainerLow,
    borderRadius: radius.md,
  },
});

export default FillBlankQuestion;
